'use client';
import { createClient } from '@/lib/supabase/client';
import { getCurrentStoreId } from '@/lib/services/storeContext';
import { setStoreContext } from '@/lib/supabase/withStoreContext';
import bcrypt from 'bcryptjs';

export type TeamRole = 'admin' | 'manager' | 'cashier' | 'warehouse';

export interface TeamMember {
  id: string;
  email: string;
  fullName: string;
  role: TeamRole;
  username: string;
  lastSignIn: string | null;
  createdAt: string;
  passwordHint: string;
  salesPointId?: string | null;
}

export interface TeamInvitation {
  id: string;
  email: string;
  role: TeamRole;
  status: 'pending' | 'accepted' | 'expired';
  expiresAt: string;
  createdAt: string;
}

export interface RolePermission {
  module: string;
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  /** 'own' = only own data, 'company' = all company data */
  scope?: 'own' | 'company';
}

export type PermissionsMap = Record<TeamRole, Record<string, RolePermission>>;

export interface CreateSubUserPayload {
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  role: TeamRole;
  salesPointId?: string;
}

export const MODULES = [
  { key: 'dashboard',           labelFr: 'Ventes du jour (Vendeur)',   labelEn: 'Daily Sales (Seller)' },
  { key: 'pos',                 labelFr: 'Point de vente',             labelEn: 'Point of Sale' },
  { key: 'off_stock_sale',      labelFr: 'Vente hors stock',           labelEn: 'Off-Stock Sale' },
  { key: 'inventory',           labelFr: 'Inventaire / Produits',      labelEn: 'Inventory / Products' },
  { key: 'inventory_state',     labelFr: 'État d\'inventaire',         labelEn: 'Inventory State' },
  { key: 'client_transactions', labelFr: 'Transactions clients',       labelEn: 'Client Transactions' },
  { key: 'expenses',            labelFr: 'Dépenses',                   labelEn: 'Expenses' },
  { key: 'finance',             labelFr: 'Finance',                    labelEn: 'Finance' },
  { key: 'reports',             labelFr: 'Rapports',                   labelEn: 'Reports' },
  { key: 'sales_points',        labelFr: 'Points de vente',            labelEn: 'Sales Points' },
  { key: 'pieces_comptables',   labelFr: 'Pièces comptables',          labelEn: 'Accounting Vouchers' },
  { key: 'inventory_vouchers',  labelFr: "Bons d\'inventaire",          labelEn: 'Inventory Vouchers' },
  { key: 'warehouse',           labelFr: 'Dépôt / Entrepôt',          labelEn: 'Warehouse / Depot' },
  { key: 'settings',            labelFr: 'Paramètres',                 labelEn: 'Settings' },
  { key: 'administration',      labelFr: 'Administration',             labelEn: 'Administration' },
  // Legacy / sub-modules kept for backward compat
  { key: 'purchase',            labelFr: 'Achat',                      labelEn: 'Purchase' },
  { key: 'sales',               labelFr: 'Ventes & Analytique',        labelEn: 'Sales & Analytics' },
  { key: 'clients',             labelFr: 'Clients',                    labelEn: 'Clients' },
  { key: 'suppliers',           labelFr: 'Fournisseurs',               labelEn: 'Suppliers' },
  { key: 'supplier_reports',    labelFr: 'Rapports fournisseurs',      labelEn: 'Supplier Reports' },
  { key: 'categories',          labelFr: 'Catégories',                 labelEn: 'Categories' },
  { key: 'barcode',             labelFr: 'Codes-barres',               labelEn: 'Barcodes' },
  { key: 'troc',                labelFr: 'Mode Troc',                  labelEn: 'Barter Mode' },
  { key: 'tva',                 labelFr: 'TVA',                        labelEn: 'VAT' },
  { key: 'discounts',           labelFr: 'Remises & Réductions',       labelEn: 'Discounts' },
  { key: 'messaging',           labelFr: 'E-easy Messagerie',          labelEn: 'E-easy Messaging' },
  { key: 'invoice_repository',  labelFr: 'Répertoire des factures',    labelEn: 'Invoice Repository' },
  { key: 'payments',            labelFr: 'Paiements & Créances',       labelEn: 'Payments & Receivables' },
  { key: 'proforma',            labelFr: 'Factures Proforma',          labelEn: 'Proforma Invoices' },
  { key: 'cancel_pos_invoice',  labelFr: 'Annuler facture POS',        labelEn: 'Cancel POS Invoice' },
  { key: 'pos_expenses',        labelFr: 'Dépenses POS',               labelEn: 'POS Expenses' },
];

/** Primary modules shown in the permission UI (sidebar order) */
export const PRIMARY_MODULES = [
  'dashboard',
  'pos',
  'off_stock_sale',
  'inventory',
  'inventory_state',
  'client_transactions',
  'expenses',
  'finance',
  'reports',
  'sales_points',
  'pieces_comptables',
  'inventory_vouchers',
  'warehouse',
  'settings',
  'administration',
  'cancel_pos_invoice',
  'pos_expenses',
];

/** Sub-menus per module — used for granular per-sub-menu permission toggles */
export interface SubMenu {
  key: string;
  labelFr: string;
  labelEn: string;
}

export const MODULE_SUBMENUS: Record<string, SubMenu[]> = {
  pieces_comptables: [
    { key: 'invoices',          labelFr: 'Factures',                   labelEn: 'Invoices' },
    { key: 'invoice_search',    labelFr: 'Recherche factures',         labelEn: 'Invoice Search' },
    { key: 'clients_pc',        labelFr: 'Clients',                    labelEn: 'Clients' },
    { key: 'suppliers_pc',      labelFr: 'Fournisseurs',               labelEn: 'Suppliers' },
    { key: 'supplier_reports',  labelFr: 'Rapport fournisseurs',       labelEn: 'Supplier Reports' },
    { key: 'payments_pc',       labelFr: 'Paiements',                  labelEn: 'Payments' },
    { key: 'proforma_pc',       labelFr: 'Pro Forma',                  labelEn: 'Pro Forma' },
    { key: 'sales_analytics',   labelFr: 'Analytiques ventes',         labelEn: 'Sales Analytics' },
    { key: 'customer_mgmt',     labelFr: 'Gestion clients',            labelEn: 'Customer Management' },
  ],
  inventory_vouchers: [
    { key: 'products',          labelFr: 'Produits',                   labelEn: 'Products' },
    { key: 'inventory_iv',      labelFr: 'Inventaire',                 labelEn: 'Inventory' },
    { key: 'inventory_state',   labelFr: "État d'inventaire",          labelEn: 'Inventory State' },
    { key: 'purchase_iv',       labelFr: 'Achat',                      labelEn: 'Purchase' },
  ],
  reports: [
    { key: 'sales_report',      labelFr: 'Rapport des ventes',         labelEn: 'Sales Report' },
    { key: 'product_catalog',   labelFr: 'Catalogue produits',         labelEn: 'Product Catalog' },
    { key: 'client_report',     labelFr: 'Rapport clients',            labelEn: 'Client Report' },
    { key: 'stock_movement',    labelFr: 'Mouvements de stock',        labelEn: 'Stock Movements' },
    { key: 'daily_sales',       labelFr: 'Ventes journalières',        labelEn: 'Daily Sales' },
  ],
  pos: [
    { key: 'pos_sale',          labelFr: 'Vente',                      labelEn: 'Sale' },
    { key: 'pos_my_stock',      labelFr: 'Mon stock',                  labelEn: 'My Stock' },
    { key: 'pos_other_stocks',  labelFr: 'Autres stocks',              labelEn: 'Other Stocks' },
  ],
  inventory: [
    { key: 'stock_view',        labelFr: 'Voir le stock',              labelEn: 'View Stock' },
    { key: 'stock_movement_inv',labelFr: 'Mouvements',                 labelEn: 'Movements' },
    { key: 'stock_adjust',      labelFr: 'Ajustement',                 labelEn: 'Adjustment' },
  ],
  clients: [
    { key: 'client_list',       labelFr: 'Liste clients',              labelEn: 'Client List' },
    { key: 'client_profile',    labelFr: 'Profil client',              labelEn: 'Client Profile' },
    { key: 'client_history',    labelFr: 'Historique achats',          labelEn: 'Purchase History' },
  ],
  expenses: [
    { key: 'expense_list',      labelFr: 'Liste des dépenses',         labelEn: 'Expense List' },
    { key: 'expense_add',       labelFr: 'Ajouter une dépense',        labelEn: 'Add Expense' },
  ],
  payments: [
    { key: 'payment_list',      labelFr: 'Liste des paiements',        labelEn: 'Payment List' },
    { key: 'credit_management', labelFr: 'Gestion des crédits',        labelEn: 'Credit Management' },
  ],
  warehouse: [
    { key: 'warehouse_stock',   labelFr: 'Stock entrepôt',             labelEn: 'Warehouse Stock' },
    { key: 'warehouse_transfer',labelFr: 'Transferts',                 labelEn: 'Transfers' },
    { key: 'warehouse_purchase',labelFr: 'Achats entrepôt',            labelEn: 'Warehouse Purchases' },
    { key: 'warehouse_reports', labelFr: 'Rapports entrepôt',          labelEn: 'Warehouse Reports' },
  ],
  sales: [
    { key: 'sales_list',        labelFr: 'Liste des ventes',           labelEn: 'Sales List' },
    { key: 'sales_analytics_tab',labelFr: 'Analytiques',               labelEn: 'Analytics' },
  ],
  suppliers: [
    { key: 'supplier_list',     labelFr: 'Liste fournisseurs',         labelEn: 'Supplier List' },
    { key: 'supplier_history',  labelFr: 'Historique fournisseur',     labelEn: 'Supplier History' },
    { key: 'supplier_delivery', labelFr: 'Livraisons',                 labelEn: 'Deliveries' },
  ],
  finance: [
    { key: 'finance_dashboard', labelFr: 'Tableau de bord Finance',   labelEn: 'Finance Dashboard' },
    { key: 'finance_ledger',    labelFr: 'Grand livre',                labelEn: 'General Ledger' },
    { key: 'finance_pnl',       labelFr: 'Rapport P&L',               labelEn: 'P&L Report' },
    { key: 'finance_capital',   labelFr: 'Apports en capital',        labelEn: 'Capital Contributions' },
  ],
  reports: [
    { key: 'sales_report',      labelFr: 'Rapport des ventes',         labelEn: 'Sales Report' },
    { key: 'product_catalog',   labelFr: 'Catalogue produits',         labelEn: 'Product Catalog' },
    { key: 'client_report',     labelFr: 'Rapport clients',            labelEn: 'Client Report' },
    { key: 'stock_movement',    labelFr: 'Mouvements de stock',        labelEn: 'Stock Movements' },
    { key: 'daily_sales',       labelFr: 'Ventes journalières',        labelEn: 'Daily Sales' },
  ],
  settings: [
    { key: 'settings_business', labelFr: 'Infos entreprise',          labelEn: 'Business Info' },
    { key: 'settings_appearance',labelFr: 'Apparence',                 labelEn: 'Appearance' },
    { key: 'settings_invoice',  labelFr: 'Paramètres facture',        labelEn: 'Invoice Settings' },
    { key: 'settings_categories',labelFr: 'Catégories',               labelEn: 'Categories' },
    { key: 'settings_exchange', labelFr: 'Taux de change',            labelEn: 'Exchange Rates' },
    { key: 'settings_backup',   labelFr: 'Sauvegarde & Export',       labelEn: 'Backup & Export' },
  ],
  administration: [
    { key: 'admin_dashboard',   labelFr: 'Tableau de bord Admin',     labelEn: 'Admin Dashboard' },
    { key: 'admin_users',       labelFr: 'Gestion utilisateurs',      labelEn: 'User Management' },
    { key: 'admin_audit',       labelFr: 'Journaux d\'audit',         labelEn: 'Audit Logs' },
  ],
};

// Modules accessible to manager by default (view + add/edit for operational ones)
const MANAGER_VIEW_MODULES = ['pos', 'off_stock_sale', 'inventory', 'clients', 'suppliers', 'sales', 'reports', 'expenses', 'supplier_reports', 'client_transactions', 'categories', 'barcode', 'troc', 'tva', 'discounts', 'messaging', 'invoice_repository', 'payments', 'proforma'];
const MANAGER_EDIT_MODULES = ['pos', 'off_stock_sale', 'inventory', 'clients', 'suppliers', 'expenses', 'categories', 'barcode', 'troc', 'tva', 'discounts', 'payments', 'proforma'];

// Modules accessible to cashier by default (view only for most, add for pos)
// 'dashboard' is NOT in CASHIER_VIEW_MODULES by default — admin must explicitly grant it
// 'off_stock_sale' is NOT in CASHIER_VIEW_MODULES by default — admin must explicitly grant it
const CASHIER_VIEW_MODULES = ['pos', 'inventory', 'clients', 'categories', 'barcode', 'troc', 'tva', 'discounts', 'messaging', 'proforma'];
const CASHIER_ADD_MODULES = ['pos', 'proforma'];

export const DEFAULT_PERMISSIONS: Record<TeamRole, Record<string, RolePermission>> = {
  admin: Object.fromEntries(
    MODULES.map((m) => [m.key, { module: m.key, canView: true, canAdd: true, canEdit: true, canDelete: true }])
  ),
  manager: Object.fromEntries(
    MODULES.map((m) => [
      m.key,
      {
        module: m.key,
        canView: MANAGER_VIEW_MODULES.includes(m.key),
        canAdd: MANAGER_EDIT_MODULES.includes(m.key),
        canEdit: MANAGER_EDIT_MODULES.includes(m.key),
        canDelete: false,
      },
    ])
  ),
  cashier: Object.fromEntries(
    MODULES.map((m) => [
      m.key,
      {
        module: m.key,
        canView: CASHIER_VIEW_MODULES.includes(m.key),
        canAdd: CASHIER_ADD_MODULES.includes(m.key),
        canEdit: false,
        canDelete: false,
      },
    ])
  ),
  warehouse: Object.fromEntries(
    MODULES.map((m) => [
      m.key,
      {
        module: m.key,
        canView: m.key === 'warehouse' || m.key === 'inventory',
        canAdd: m.key === 'warehouse' || m.key === 'inventory',
        canEdit: m.key === 'warehouse',
        canDelete: m.key === 'warehouse',
      },
    ])
  ),
};

// ── Team Members ─────────────────────────────────────────────

export async function fetchTeamMembers(): Promise<TeamMember[]> {
  const supabase = createClient();
  const storeId = await getCurrentStoreId();
  if (!storeId) return [];
  await setStoreContext(supabase, storeId);

  const { data, error } = await supabase
    .from('ef_app_users')
    .select('id, email, full_name, role, username, last_sign_in, created_at, plain_password_hint, sales_point_id')
    .eq('store_id', storeId)
    .neq('role', 'admin')
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);

  return (data ?? []).map((u: any) => ({
    id: u.id,
    email: u.email ?? '',
    fullName: u.full_name ?? '',
    role: (u.role as TeamRole) ?? 'cashier',
    username: u.username ?? '',
    lastSignIn: u.last_sign_in,
    createdAt: u.created_at,
    passwordHint: u.plain_password_hint ?? '',
    salesPointId: u.sales_point_id ?? null,
  }));
}

/** Fetch all team members including admin — used for cashier filter in reports */
export async function fetchAllTeamMembersIncludingAdmin(): Promise<TeamMember[]> {
  const supabase = createClient();
  const storeId = await getCurrentStoreId();
  if (!storeId) return [];
  await setStoreContext(supabase, storeId);

  const { data, error } = await supabase
    .from('ef_app_users')
    .select('id, email, full_name, role, username, last_sign_in, created_at, plain_password_hint')
    .eq('store_id', storeId)
    .order('role', { ascending: true }) // admin first
    .order('created_at', { ascending: false });

  if (error) return [];

  return (data ?? []).map((u: any) => ({
    id: u.id,
    email: u.email ?? '',
    fullName: u.full_name ?? '',
    role: (u.role as TeamRole) ?? 'cashier',
    username: u.username ?? '',
    lastSignIn: u.last_sign_in,
    createdAt: u.created_at,
    passwordHint: u.plain_password_hint ?? '',
  }));
}

export interface StoreCredential {
  id: string;
  fullName: string;
  username: string;
  role: TeamRole;
  plainPassword: string;
  createdAt: string;
  isActive: boolean;
}

export async function fetchStoreCredentials(storeId: string): Promise<StoreCredential[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('ef_app_users')
    .select('id, full_name, username, role, plain_password_hint, created_at, is_active')
    .eq('store_id', storeId)
    .order('created_at', { ascending: true });

  if (error) throw new Error(error.message);

  return (data ?? []).map((u: any) => ({
    id: u.id,
    fullName: u.full_name ?? '',
    username: u.username ?? '',
    role: (u.role as TeamRole) ?? 'cashier',
    plainPassword: u.plain_password_hint ?? '',
    createdAt: u.created_at,
    isActive: u.is_active !== false,
  }));
}

export async function updateMemberRole(memberId: string, role: TeamRole): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('ef_app_users')
    .update({ role, updated_at: new Date().toISOString() })
    .eq('id', memberId);
  if (error) throw new Error(error.message);
}

export async function updateMemberSalesPoint(memberId: string, salesPointId: string | null): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('ef_app_users')
    .update({ sales_point_id: salesPointId, updated_at: new Date().toISOString() })
    .eq('id', memberId);
  if (error) throw new Error(error.message);
}

export async function removeMember(memberId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('ef_app_users').delete().eq('id', memberId);
  if (error) throw new Error(error.message);
}

// ── Invitations ───────────────────────────────────────────────

export async function fetchInvitations(): Promise<TeamInvitation[]> {
  const supabase = createClient();
  const storeId = await getCurrentStoreId();
  if (!storeId) return [];

  const { data, error } = await supabase
    .from('ef_team_invitations')
    .select('id, email, role, status, expires_at, created_at')
    .eq('store_id', storeId)
    .order('created_at', { ascending: false });

  if (error) {
    // Table may not exist yet
    return [];
  }

  return (data ?? []).map((inv: any) => ({
    id: inv.id,
    email: inv.email,
    role: inv.role as TeamRole,
    status: inv.status,
    expiresAt: inv.expires_at,
    createdAt: inv.created_at,
  }));
}

export async function sendInvitation(email: string, role: TeamRole): Promise<void> {
  const supabase = createClient();
  const storeId = await getCurrentStoreId();
  if (!storeId) throw new Error('No store found');
  await setStoreContext(supabase, storeId);

  const { error } = await supabase.from('ef_team_invitations').insert({
    store_id: storeId,
    email: email.toLowerCase().trim(),
    role,
  });

  if (error) throw new Error(error.message);
}

export async function cancelInvitation(invitationId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('ef_team_invitations')
    .update({ status: 'expired', updated_at: new Date().toISOString() })
    .eq('id', invitationId);
  if (error) throw new Error(error.message);
}

// ── Role Permissions ──────────────────────────────────────────

export async function fetchRolePermissions(): Promise<PermissionsMap> {
  const supabase = createClient();
  const storeId = await getCurrentStoreId();
  if (!storeId) return DEFAULT_PERMISSIONS;

  const { data, error } = await supabase
    .from('ef_role_permissions')
    .select('role, module, can_view, can_add, can_edit, can_delete')
    .eq('store_id', storeId);

  if (error || !data || data.length === 0) {
    return DEFAULT_PERMISSIONS;
  }

  const map: PermissionsMap = {
    admin: { ...DEFAULT_PERMISSIONS.admin },
    manager: { ...DEFAULT_PERMISSIONS.manager },
    cashier: { ...DEFAULT_PERMISSIONS.cashier },
    warehouse: { ...DEFAULT_PERMISSIONS.warehouse },
  };

  for (const row of data) {
    const role = row.role as TeamRole;
    if (map[role]) {
      map[role][row.module] = {
        module: row.module,
        canView: row.can_view,
        canAdd: row.can_add,
        canEdit: row.can_edit,
        canDelete: row.can_delete,
      };
    }
  }

  return map;
}

export async function saveRolePermissions(
  role: TeamRole,
  permissions: Record<string, RolePermission>
): Promise<void> {
  const supabase = createClient();
  const storeId = await getCurrentStoreId();
  if (!storeId) throw new Error('No store found');
  await setStoreContext(supabase, storeId);

  const rows = Object.values(permissions).map((p) => ({
    store_id: storeId,
    role,
    module: p.module,
    can_view: p.canView,
    can_add: p.canAdd,
    can_edit: p.canEdit,
    can_delete: p.canDelete,
    updated_at: new Date().toISOString(),
  }));

  const { error } = await supabase
    .from('ef_role_permissions')
    .upsert(rows, { onConflict: 'store_id,role,module' });

  if (error) throw new Error(error.message);
}

// ── Create Sub-User (seller / manager) ───────────────────────

export async function createSubUser(payload: CreateSubUserPayload): Promise<void> {
  const supabase = createClient();
  const storeId = await getCurrentStoreId();
  if (!storeId) throw new Error('No store found');
  await setStoreContext(supabase, storeId);

  const syntheticEmail = `${payload.username}.${storeId.slice(0, 8)}@easyframe.local`;
  const fullName = `${payload.firstName} ${payload.lastName}`.trim();
  const passwordHash = await bcrypt.hash(payload.password, 10);

  const insertData: any = {
    store_id: storeId,
    email: syntheticEmail,
    full_name: fullName,
    role: payload.role,
    username: payload.username,
    plain_password_hint: payload.password,
    password_hash: passwordHash,
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  if (payload.salesPointId) {
    insertData.sales_point_id = payload.salesPointId;
  }

  const { error } = await supabase.from('ef_app_users').insert([insertData]);

  if (error) throw new Error(error.message);
}

// ── Generate / Reset Store Admin Credentials (super-admin only) ──────────────

export interface GeneratedCredentials {
  username: string;
  password: string;
}

function generateStorePassword(): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const special = '@#$!';
  const all = upper + lower + digits + special;
  let pwd = '';
  pwd += upper[Math.floor(Math.random() * upper.length)];
  pwd += lower[Math.floor(Math.random() * lower.length)];
  pwd += digits[Math.floor(Math.random() * digits.length)];
  pwd += special[Math.floor(Math.random() * special.length)];
  for (let i = 4; i < 10; i++) {
    pwd += all[Math.floor(Math.random() * all.length)];
  }
  return pwd.split('').sort(() => Math.random() - 0.5).join('');
}

/**
 * Creates or updates the main admin credentials for a store.
 * Stores both a bcrypt hash (for secure login) and the plain text hint
 * (so the super-admin can always view and share the credentials).
 */
export async function generateStoreAdminCredentials(
  storeId: string,
  ownerName: string,
  ownerEmail: string
): Promise<GeneratedCredentials> {
  const supabase = createClient();
  await setStoreContext(supabase, storeId);

  const baseUsername = ownerName.trim().split(/\s+/)[0].toLowerCase().replace(/[^a-z0-9]/g, '');
  const username = baseUsername || ownerEmail.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
  const password = generateStorePassword();
  const syntheticEmail = `${username}.${storeId.slice(0, 8)}@easyframe.local`;
  const passwordHash = await bcrypt.hash(password, 10);

  const { data: existing } = await supabase
    .from('ef_app_users')
    .select('id')
    .eq('store_id', storeId)
    .eq('role', 'admin')
    .maybeSingle();

  if (existing?.id) {
    const { error } = await supabase
      .from('ef_app_users')
      .update({
        username,
        plain_password_hint: password,
        password_hash: passwordHash,
        is_active: true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', existing.id);
    if (error) throw new Error(error.message);
  } else {
    const { error } = await supabase.from('ef_app_users').insert([{
      store_id: storeId,
      email: syntheticEmail,
      full_name: ownerName.trim(),
      role: 'admin',
      username,
      plain_password_hint: password,
      password_hash: passwordHash,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }]);
    if (error) throw new Error(error.message);
  }

  return { username, password };
}
